
/**
 * Module dependencies.
 */

import { Button as DropdownButton, Wrapper as DropdownWrapper } from 'react-aria-menubutton';
import { DropdownMenuItem, StyledMenu } from './dropdown-menu';
import { Icon } from 'react-components/media';
import { color } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import { useCallback, useState } from 'react';
import { useTranslate } from '../i18n';
import caretIcon from './assets/svg/16/caret.svg';
import styled, { css } from 'styled-components';

/**
 * `Value` type.
 */

type Value = 'all' | null;

/**
 * `Props` type.
 */

type Props = {
  alignDropdown?: 'left' | 'right';
  className?: string;
  onSelect: (value: Value) => void;
  totalItems: number;
  totalItemsSelected: number;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ hasItemsSelected: boolean }>`
  align-items: center;
  background-color: transparent;
  border-radius: ${theme('dimensions.borderRadius')}px;
  display: grid;
  grid-column-gap: 4px;
  grid-template-areas: 'checkbox dropdown';
  grid-template-columns: repeat(2, max-content);
  height: 36px;
  justify-content: center;
  transition: background-color ${theme('animations.defaultTransition')};
  white-space: nowrap;
  width: 52px;

  ${ifProp('hasItemsSelected', css`
    background-color: ${color('gray300')};
  `)}
`;

/**
 * `Checkbox` styled component.
 */

const Checkbox = styled.span`
  cursor: pointer;
  grid-area: checkbox;
  height: 24px;
  width: 24px;
`;

/**
 * `StyledDropdownWrapper` styled component.
 */

const StyledDropdownWrapper = styled(DropdownWrapper)`
  grid-area: dropdown;
  height: 24px;
  position: relative;
`;

/**
 * `StyledDropdownButton` styled component.
 */

const StyledDropdownButton = styled(DropdownButton)`
  color: ${color('gray500')};
  cursor: pointer;
  outline-width: 0;
  position: relative;
  transition: color ${theme('animations.defaultTransition')};

  &:focus-visible {
    outline-width: 4px;
  }

  &:-moz-focusring {
    outline-width: 1px;
  }

  &:focus,
  &:hover {
    color: ${color('primary')};
  }
`;

/**
 * `Caret` styled component.
 */

const Caret = styled(Icon).attrs({ 'aria-hidden': true, icon: caretIcon })`
  top: 4px;
`;

/**
 * `Path` styled component.
 */

const Path = styled.path`
  fill: ${props => (props as unknown as { fill: string }).fill};
  stroke: ${props => (props as unknown as { stroke: string }).stroke};
`;

/**
 * `Rect` styled component.
 */

const Rect = styled.rect`
  fill: ${props => (props as unknown as { fill: string }).fill};
  stroke: ${props => (props as unknown as { stroke: string }).stroke};
`;

/**
 * Export `SelectAll` component.
 */

export function SelectAll(props: Props): JSX.Element {
  const { alignDropdown = 'left', className, onSelect, totalItems, totalItemsSelected } = props;
  const { translate } = useTranslate('common');
  const hasItems = totalItems > 0 && totalItemsSelected > 0;
  const [selectedValue, setValue] = useState<Value>(null);
  const handleClickDropdownMenuItem = useCallback((value: Value) => {
    if (onSelect) {
      onSelect(value);
      setValue(value);
    }
  }, [onSelect]);

  const handleClickCheckbox = useCallback(() => {
    const value = selectedValue === 'all' ? null : 'all';

    if (onSelect) {
      onSelect(value);
      setValue(value);
    }
  }, [onSelect, selectedValue]);

  return (
    <Wrapper
      className={className}
      hasItemsSelected={hasItems}
    >
      <Checkbox onClick={handleClickCheckbox}>
        <svg
          viewBox={'0 0 24 24'}
          xmlns={'http://www.w3.org/2000/svg'}
        >
          <Rect
            fill={totalItemsSelected > 0 ? color('primary') : 'none'}
            height={'18'}
            rx={'3'}
            stroke={totalItemsSelected > 0 ? color('primary') : color('gray500')}
            strokeWidth={'2'}
            width={'18'}
            x={'3'}
            y={'3'}
          />

          {hasItems && totalItemsSelected < totalItems && (
            <Path
              d={'M18.4947 11.9995H5.50415'}
              stroke={color('white')}
              strokeMiterlimit={'10'}
              strokeWidth={'2'}
            />
          )}

          {hasItems && totalItemsSelected === totalItems && (
            <Path
              d={'m17.6875 8.24194-5.388 6.61326c-.4207.5119-.9915.833-1.6069.9032H9.848c-.61546-.0702-1.18701-.3913-1.6069-.9032l-1.9286-2.3166'}
              stroke={color('white')}
              strokeMiterlimit={'10'}
              strokeWidth={'2'}
            />
          )}
        </svg>
      </Checkbox>

      <StyledDropdownWrapper>
        <StyledDropdownButton>
          <Caret size={'16px'} />
        </StyledDropdownButton>

        <StyledMenu align={alignDropdown}>
          <>
            <DropdownMenuItem onClick={() => handleClickDropdownMenuItem('all')}>
              {translate('common.selectAll')}
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => handleClickDropdownMenuItem(null)}>
              {translate('common.none')}
            </DropdownMenuItem>
          </>
        </StyledMenu>
      </StyledDropdownWrapper>
    </Wrapper>
  );
}
